import React, { useState, useEffect, useMemo } from 'react'
import {
  forceSimulation,
  forceManyBody,
  forceCenter,
  forceCollide,
  forceX,
  forceY,
} from 'd3'

import DEIPageClusterChartCanvas from './DEIPageClusterChartCanvas/DEIPageClusterChartCanvas'

const DEIPageClusterChart = ({
  colors,
  percentages,
  activeType = null,
}) => {
  const r = 17
  const width = 750
  const height = 750

  const grid = useMemo(() => {
    const grid = []
    const gridSize = (r * 2) + 4
    const gridWidth = Math.floor(width / gridSize)
    const gridHeight = Math.floor(height / gridSize * 1.17)

    for (let i = 0; i < gridWidth; i++) {
      for (let j = 0; j < gridHeight; j++) {
        const x = i * gridSize + (j % 2) * gridSize * 0.5
        const y = j * gridSize * 0.85
        grid.push({
          isOccupied: false,
          x,
          y
        })
      }
    }

    return grid
  }, [width, height])

  const initialCircles = useMemo(() => {
    // Create a flat map
    const _circles = percentages
      .map(
        ({ percentage, title }, i) =>
          !!Number(percentage) ?
            Array(Number(percentage) * 3)
              .fill(null)
              .map((_, j) => ({
                color: colors[i].HEX,
                filtered: false,
                key: `${title}-${j}`,
                r,
                title,
                type: i,
                x: Math.random() * width * Math.PI * 0.29,
                y: Math.random() * height * Math.PI * 0.29,
              })) : []
      )
      .flat()
    // Randomly shuffle the circles
    for (let i = 0; i < _circles.length; i++) {
      const j = Math.floor(Math.random() * i)
      const temp = _circles[i]
      _circles[i] = _circles[j]
      _circles[j] = temp
    }
    return _circles
  }, [percentages, colors])

  const [circlesStore, setCirclesStore] = useState(initialCircles)
  const [circlesLive, setCirclesLive] = useState(circlesStore)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (activeType === null) {
      setCirclesStore(initialCircles)
    } else {
      setCirclesStore(
        initialCircles.filter(
          circle => activeType !== null && activeType === circle.type
        )
      )
    }
  }, [activeType, initialCircles])

  useEffect(() => {
    const _circles = JSON.parse(JSON.stringify(circlesStore))

    const simulation = forceSimulation(_circles)
      .alphaMin(0.09)
      .alphaDecay(0.05)
      .force('charge', forceManyBody().strength(-40))
      .force('center', forceCenter(width / 2, height / 2))
      .force(
        'collision',
        forceCollide()
          .radius(r + 2)
          .strength(0.95)
          .iterations(40)
      )
      .force('x', forceX().x(width / 2).strength(0.15))
      .force('y', forceY().y(height / 2).strength(0.15))
      .on('tick', () => {
        setCirclesLive(_circles)
      })

    setIsInitialized(true)

    return () => {
      simulation.stop()
    }
  }, [circlesStore, grid])

  return (
    <DEIPageClusterChartCanvas
      circles={circlesLive}
      height={height}
      isInitialized={isInitialized}
      width={width}
    />
  )
}

export default DEIPageClusterChart
